import { useState } from 'react'

import { Button } from '@factorialco/deprecated-design-system'

import Popover from 'modules/core/components/PopoverMenu'
import buildUrl from 'modules/core/lib/buildUrl'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import ResendSignatureReminderModal from 'modules/documents/components/BulkActionsDatatable/ResendSignatureReminderModal'
import TrashBinModal from 'modules/documents/components/BulkActionsDatatable/TrashBinModal'
import UpdateCompanyFolderModal from 'modules/documents/components/BulkActionsDatatable/UpdateCompanyFolderModal'
import UpdateFolderModal from 'modules/documents/components/BulkActionsDatatable/UpdateFolderModal'
import useBulkUpdateFolderDocument from 'modules/documents/hooks/documents/useBulkUpdateFolderDocument'
import { Document } from 'modules/documents/hooks/documents/useGetDocuments'
import useMoveToTrashBinDocument from 'modules/documents/hooks/documents/useMoveToTrashBinDocument'
import { isCompanySpace, isPendingSignature } from 'modules/documents/lib/Documents'
import { useBulkResendRequestSignature } from 'modules/esignatures/hooks/signatures/useBulkResendRequestSignature'

interface Props {
  document: Document
  fromDataTable?: boolean
  disabled?: boolean
  space: string
}

const MoreActions = ({ document, fromDataTable = false, disabled = false, space }: Props) => {
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [resendSignatureConfirmationModalOpen, setResendSignatureConfirmationModalOpen] =
    useState(false)
  const [selectFolderModalOpen, setSelectFolderModalOpen] = useState(false)
  const { moveDocumentToTrashBin, loading: loadingTrashBin } = useMoveToTrashBinDocument({
    fromDataTable,
  })
  const { bulkResendRequestSignature, loading: loadingResendSignature } =
    useBulkResendRequestSignature()
  const { bulkUpdateFolder, loading: loadingUpdateFolder } = useBulkUpdateFolderDocument({
    fromDataTable,
  })

  const isCompanySpaceVar = isCompanySpace(space)

  const canDelete = document._policies?.edit.can ?? false

  const canUpdateFolder = document._policies?.edit.can
  const canResendSignatureReminder = isPendingSignature(document) && document.status !== 'to_fill'

  const onSubmit = async () => {
    const result = await moveDocumentToTrashBin({ documentIds: [document.id] })

    const handled = handleResult(result, () => undefined)

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('common.removed_successfully'),
      })
    } else {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('errors.generic_saving'),
      })
    }

    setDeleteModalOpen(false)
  }

  const onSubmitResendSignatureReminder = async () => {
    const result = await bulkResendRequestSignature({ documentIds: [document.id] })

    const handled = handleResult(result, () => undefined)

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.tp('documents.employee.bulk-actions.resend-signature.success', {
          count: document?.signees?.length || 1,
        }),
      })
    } else {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('errors.generic_error'),
      })
    }

    setResendSignatureConfirmationModalOpen(false)
  }

  const onSubmitUpdateFolder = async (selectedFolderId: number | null) => {
    const result = await bulkUpdateFolder({
      documentIds: [document.id],
      folderId: selectedFolderId,
    })

    const handled = handleResult(result, () => undefined)

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('documents.employee.bulk-actions.update-folder.success'),
      })
    } else {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('errors.generic_error'),
      })
    }

    setSelectFolderModalOpen(false)
  }

  return (
    <>
      <Popover type="list">
        <Popover.Trigger disabled={disabled}>
          <Button.Decorative type="secondary" iconName="ellipsis" disabled={disabled} />
        </Popover.Trigger>
        <Popover.Content side="bottom" align="end">
          {canUpdateFolder && (
            <Popover.TouchableOption
              icon="folder"
              onClick={() => setSelectFolderModalOpen(true)}
              title={i18n.t('documents.move')}
            />
          )}
          {canResendSignatureReminder && (
            <Popover.TouchableOption
              icon="bell"
              onClick={() => setResendSignatureConfirmationModalOpen(true)}
              title={i18n.t('documents.employee.bulk-actions.resend_signature_reminder')}
            />
          )}
          <Popover.HrefOption
            icon="download"
            href={buildUrl(document.file)}
            title={i18n.t('documents.download')}
          />
          {canDelete && !document.folder?.showOnly && (
            <Popover.TouchableOption
              icon="delete"
              onClick={() => setDeleteModalOpen(true)}
              title={i18n.t('documents.move_to_trash_bin')}
              danger
            />
          )}
        </Popover.Content>
      </Popover>
      {deleteModalOpen && (
        <TrashBinModal
          filename={document.filename}
          onClose={() => setDeleteModalOpen(false)}
          onSubmit={onSubmit}
          loading={loadingTrashBin}
          hasSignature={document.signatureStatus !== null}
        />
      )}
      {resendSignatureConfirmationModalOpen && (
        <ResendSignatureReminderModal
          totalSelectedDocuments={1}
          onClose={() => setResendSignatureConfirmationModalOpen(false)}
          onSubmit={onSubmitResendSignatureReminder}
          loading={loadingResendSignature}
        />
      )}
      {selectFolderModalOpen && !isCompanySpaceVar && (
        <UpdateFolderModal
          documents={[document]}
          onClose={() => setSelectFolderModalOpen(false)}
          onSubmit={onSubmitUpdateFolder}
          loading={loadingUpdateFolder}
        />
      )}
      {selectFolderModalOpen && isCompanySpaceVar && (
        <UpdateCompanyFolderModal
          documents={[document]}
          onClose={() => setSelectFolderModalOpen(false)}
          onSubmit={onSubmitUpdateFolder}
          space={space}
        />
      )}
    </>
  )
}

export default MoreActions
